import { useCallback, useEffect, useRef, useState } from 'react';
import { useAutoRefresh } from './useAutoRefresh';
import { usePollingLoad } from './usePollingLoad';
import { api } from '@/api/client';
import type { DiagnosticsData } from '@/types';
import { consumeDiagnosticsStale } from '@/utils/diagnosticsRefresh';
import { diagnosticsPollIntervalMs } from './useDiagnosticsPolling.utils';

export {
  DIAGNOSTICS_POLL_HIDDEN_MS,
  DIAGNOSTICS_POLL_VISIBLE_MS,
} from './useDiagnosticsPolling.utils';

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

type Options = {
  enabled?: boolean;
};

export function useDiagnosticsPolling({ enabled = true }: Options = {}) {
  const [diagnostics, setDiagnostics] = useState<DiagnosticsData | null>(null);
  const { run, initialLoading } = usePollingLoad({
    hasData: () => Boolean(diagnostics?.checks?.length),
  });

  const abortRef = useRef<AbortController | null>(null);
  const initialStaleConsumedRef = useRef(false);

  const fetchDiagnostics = useCallback(async (background: boolean) => {
    await run(background, async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const data = await api.dashboard.diagnostics({ signal: controller.signal });
        if (controller.signal.aborted) return;
        setDiagnostics(data);
      } catch (e) {
        if (isAbortError(e) || controller.signal.aborted) return;
        throw e instanceof Error ? e : new Error('Failed to load diagnostics');
      }
    });
  }, [run]);

  const autoRefresh = useAutoRefresh({
    enabled,
    intervalMs: diagnosticsPollIntervalMs(true),
    pauseWhenHidden: false,
    getIntervalMs: () => diagnosticsPollIntervalMs(document.visibilityState === 'visible'),
    onRefresh: async ({ background }) => {
      if (!initialStaleConsumedRef.current) {
        initialStaleConsumedRef.current = true;
        consumeDiagnosticsStale();
      }
      await fetchDiagnostics(background);
    },
    onResume: (reason) => {
      if (reason === 'visible') {
        const stale = consumeDiagnosticsStale();
        return { background: !stale };
      }
      return undefined;
    },
    storageKey: 'diagnostics-auto-refresh',
  });

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }
  }, [enabled]);

  return {
    diagnostics,
    lastUpdatedAt: autoRefresh.lastUpdatedAt,
    loading: initialLoading,
    autoRefreshing: autoRefresh.refreshing,
    error: autoRefresh.error,
    refresh: autoRefresh.refresh,
    autoRefresh,
  };
}
